import React, { useEffect, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { matchPath } from 'react-router'
import classnames from 'classnames'
import { FormattedMessage } from 'react-intl'
import { useFormatMessage } from 'src/providers/intl'
import { OVERLAY_HELPERS } from 'src/constants/navigation'
import { ARM_MORTGAGE_TYPE, FIX_MORTGAGE_TYPE } from 'src/constants/loan'
import { resetTca, createTca } from 'src/actions/tca'
import { selectTcaSuccess } from 'src/store/selectors/tca'
import { OverlayPanel, Button, If, Unless } from 'src/Components'
import history from 'src/history'
import MortgageCoachConfirm from './MortgageCoachConfirm'
import MortgageCoachSuccess from './MortgageCoachSuccess'
import MortgageCoachFullLogoSvg from './MortgageCoachFullLogoSvg'
import { dispatchEvent } from 'src/helpers/tagManager'
import { MortgageCoachNavigateBack } from 'src/constants/trackingEvents'
import { selectCustomerIsREA } from 'src/store/selectors/customerProfile'

import styles from './MortgageCoachRequest.module.scss'

const MortgageCoachRequest = ({
  rootMatchPath,
  type,
  term,
  rate,
  years,
  className
}) => {
  const dispatch = useDispatch()
  const intlFormat = useFormatMessage()

  const overlayRef = useRef(null)
  const success = useSelector(selectTcaSuccess)
  const customerIsREA = useSelector(selectCustomerIsREA)

  const rootMatch =
    matchPath(window.location.pathname, {
      path: rootMatchPath || ''
    }) || {}
  const handleOpenConfirm = () =>
    rootMatch.url &&
    history.push(`${rootMatch.url}/${OVERLAY_HELPERS.mortgageCoach.overlay}`)
  const handleTracking = useCallback(() => {
    if (!success) {
      dispatchEvent(MortgageCoachNavigateBack, {
        eventLabel: 'Last: Request TCA',
        customerType: customerIsREA ? 'REA' : 'LO'
      })
    }
  }, [success, customerIsREA])
  const doClose = useCallback(() => {
    if (overlayRef.current) {
      overlayRef.current.back()
    }
  }, [])

  const requestTca = useCallback(() => {
    dispatch(createTca({ type, term, rate, years }))
    dispatchEvent(MortgageCoachNavigateBack, {
      eventLabel: 'Last: Finished',
      customerType: customerIsREA ? 'REA' : 'LO'
    })
  }, [type, term, rate, years, dispatch, customerIsREA])

  useEffect(() => {
    // Exact match at root (currently refi panels), means no mortgage coach routing.
    // Reset state here so we always come into MC with clean state.
    if (rootMatch.isExact) {
      dispatch(resetTca())
    }
  }, [rootMatch, dispatch])

  let refiType
  switch (type) {
    case FIX_MORTGAGE_TYPE:
      refiType = `${years}-year fixed`
      break
    case ARM_MORTGAGE_TYPE:
      refiType = `${years}/1 ARM`
      break
    default:
      refiType = `${years}-year ${type}`
      break
  }

  const MortgageCoachIcon = () => (
    <div className={styles.mcIconWrapper}>
      <MortgageCoachFullLogoSvg />
    </div>
  )

  return (
    <div className={classnames(styles.container, className)}>
      <Button className={styles.button} primary onClick={handleOpenConfirm}>
        <FormattedMessage
          id='MortgageCoach.requestTCA'
          defaultMessage='Get detailed breakdown'
        />
      </Button>
      <OverlayPanel
        ref={overlayRef}
        back={rootMatch.url || OVERLAY_HELPERS.refinance.getOrigin()}
        path={`${rootMatchPath}/${OVERLAY_HELPERS.mortgageCoach.overlay}`}
        title={OVERLAY_HELPERS.getTitle('mortgageCoach', intlFormat)}
        onClose={handleTracking}
        renderTitle={MortgageCoachIcon}
        render={() => (
          <>
            <Unless condition={success}>
              <MortgageCoachConfirm
                refiType={refiType}
                handleConfirm={requestTca}
              />
            </Unless>
            <If condition={success}>
              <MortgageCoachSuccess handleClose={doClose} />
            </If>
          </>
        )}
      />
    </div>
  )
}

export default MortgageCoachRequest
